/* eslint-disable import/no-default-export */
import React from 'react';
import classnames from 'classnames';
import {animated} from '@react-spring/web';
import {ITile} from '@dazn/lp-web/src/types/tile.types';
import {useMedia} from '@dazn/lp-web/src/hooks/useMedia';
import {tileTypeConfig} from '@dazn/lp-web/src/utils/tileConfig';
import {LockIcon} from '@dazn/lp-web/src/components/Icons/LockIcon';
import styles from '@dazn/lp-web/src/components/Rail/rail.module.css';
import {useTileTransitions} from '@dazn/lp-web/src/hooks/useTransitions';
import SimpleResponsiveImage from '@dazn/lp-web/src/components/Images/SimpleResponsiveImage/SimpleResponsiveImage';
import {triggerSelectItemList} from '@dazn/lp-web/src/analytics/fe-events';
import {navigateToDifferentChapterUrl} from '@dazn/lp-web/src/utils';
import {getWomenFootballCompetitions} from '@dazn/lp-web/src/features/getWomenFootBallCompetitionIds';
import {useLandingPagesContext} from '@dazn/lp-web/src/contexts/LandingPageContext';
import {checkIfNflAvailableEnabled} from '@dazn/lp-web/src/features/getNflAvailable';
import {getTileDateLabel} from '@dazn/lp-web/src/utils/getTileDataToString';
import {tileLabels, tileTypes} from '@dazn/lp-web/src/constants/tile.constants';
import {getDictionaryString} from '@dazn/lp-web/src/utils/dictionary';
import isQueryParamTrue from '../../utils/isQueryparamTrue';
import PlayIcon from '../PlayIcon/PlayIcon';
import {checkIfAnonymousPlaybackPermitted} from '../../utils/checkIfAnonymousPlaybackPermitted';

interface ITileProps {
    tile: ITile;
    index: number;
    slidesPerView: number;
    tiles: number;
    disableAnimation: boolean;
}
type ParsedParams = Record<string, string> | null;

function parseNavParams(params: string): ParsedParams {
    if (!params || params.length === 0) {
        return null;
    }

    return params.split(';').reduce((acc, a) => {
        const [key, val] = a.split(':');
        if (key && val) {
            acc[key.charAt(0).toLowerCase() + key.slice(1)] = val;
        }
        return acc;
    }, {} as Record<string, string>);
}

function getTileUrl(tile: ITile): string {
    const navigationParams = parseNavParams(tile.navParams);
    const pageType = navigationParams?.pageType?.toLowerCase();
    if (pageType === 'fixture') {
        return `${tile.navigateTo?.toLowerCase()}/ContentId:${
            navigationParams?.contentId
        }/${tile.eventId || ''}`;
    }
    if (pageType === 'show') {
        return `${tile.navigateTo?.toLowerCase()}/${
            navigationParams?.contentType
        }:${navigationParams?.contentId}/${tile.eventId || ''}`;
    }
    if (tile?.navigateTo) {
        return `${tile.navigateTo?.toLowerCase()}/${tile.id}`;
    }
    return `home/${tile.eventId}`;
}

const Tile: React.FC<ITileProps> = ({
    tile,
    index,
    slidesPerView,
    tiles,
    disableAnimation = false
}) => {
    const {isDesktop} = useMedia();
    const [refView, springs] = useTileTransitions();
    const {getPromoCodeByEntSetId} = useLandingPagesContext();

    const isWomenFootBallCompetition = getWomenFootballCompetitions().includes(
        tile.competitionId as string
    );

    const isFreemiumTile = !tile.entitlementIds?.length;
    const entitlementId =
        Array.isArray(tile.entitlementIds) && tile.entitlementIds?.length > 0
            ? tile.entitlementIds[0]
            : '';

    const handleTileClick = (): void => {
        const tilePayload = {
            tileTitle: tile.title || '',
            assetId: tile.assetId || '',
            assetType: 'fixture',
            tilePositionOnLoad: index + 1,
            tilePositionOnClick: index + 1,
            numberOfTilesInRail: tiles,
            entitlementId,
            entitlementIds: tile.entitlementIds,
            railTitle: '',
            railId: '',
            railPosition: '',
            competitionId: tile.competitionId || '',
            competitionName: tile.competitionName || '',
            scheduleDate: '',
            scheduleFilter: '',
            pulseType: ''
        };

        triggerSelectItemList(tilePayload);

        const tileContentType = tile.contentType;
        const promoCode = getPromoCodeByEntSetId(tile?.entitlementIds?.[0]);
        const isNlTv = tile?.entitlementIds?.includes(
            'ent_nationalleaguetv_pro_competitions'
        );

        const chapterPath = (() => {
            if (
                tileContentType.toLocaleLowerCase() === 'nfl' &&
                checkIfNflAvailableEnabled()
            ) {
                return `/account/content/${tileContentType}/signup`;
            }

            if (isNlTv) {
                return `/account/content/nationalleaguetv/signup`;
            }

            return `/account/content/dazn/signup`;
        })();

        navigateToDifferentChapterUrl({
            chapterName: 'auth',
            chapterPath,
            chapterData: {
                ...(isFreemiumTile ? {isFreemium: isFreemiumTile} : {}),
                ...(isWomenFootBallCompetition && !tile.entitlementIds?.length
                    ? {freemiumType: 'WOMENFOOTBALL'}
                    : {}),
                ...(promoCode ? {promoCode} : {}),
                deepLink: {
                    chapterName: 'catalog',
                    chapterPath: getTileUrl(tile)
                }
            }
        });
    };

    const showPadlock = !!tile?.entitlementIds?.length;

    const timeLabel = (() => {
        if (tile.type === tileTypes.LIVE) {
            return getDictionaryString(tileLabels.LIVE);
        }

        const startDate = tile.start ? new Date(tile.start) : null;
        return startDate ? getTileDateLabel(startDate, tile.type) : '';
    })();

    const animations = (() => {
        if (index >= slidesPerView || isQueryParamTrue('seo')) return {};

        return {
            style: springs,
            ref: refView
        };
    })();

    const showTileLabels = showPadlock || timeLabel;

    return (
        <animated.article {...(disableAnimation ? {} : animations)}>
            <div
                className={classnames(styles.tile, {
                    [styles.tileNonDesktop]: !isDesktop
                })}
                onClick={handleTileClick}
            >
                <header>
                    {showTileLabels && (
                        <div className={styles.tileLabels}>
                            {showPadlock && (
                                <div className={styles.padlock}>
                                    <LockIcon />
                                </div>
                            )}
                            {timeLabel && (
                                <div
                                    className={classnames(styles.date, {
                                        [styles.live]:
                                            tile.type === tileTypes.LIVE
                                    })}
                                >
                                    {timeLabel}
                                </div>
                            )}
                        </div>
                    )}
                    {checkIfAnonymousPlaybackPermitted(tile) &&
                        (tile?.isVod || tile?.type === tileTypes.LIVE) && (
                            <div className={styles.play}>
                                <PlayIcon />
                            </div>
                        )}
                    <SimpleResponsiveImage
                        imageUrl={tile.imageUrl}
                        aspectRatio={tileTypeConfig.STANDARD.aspectRatio}
                    />
                </header>
                <footer>
                    <div className={styles.title}>{tile.title}</div>
                    <div className={styles.subtitle}>{tile.label}</div>
                </footer>
            </div>
        </animated.article>
    );
};

export default Tile;
